import React, {useCallback, useEffect} from 'react'
import {Modal, ModalBody} from 'reactstrap'
import {useBackgroundRemoval, REMOVE_BG_STATUS} from '../../hooks/useBackgroundRemoval'
import {useRemoveBgEditor} from '../../hooks/useRemoveBgEditor'
import {useRemoveBgShortcuts} from '../../hooks/useRemoveBgShortcuts'
import RemoveBgEditorLayout from './remove-bg/RemoveBgEditorLayout'
import RemoveBgPreviewPanel from './remove-bg/RemoveBgPreviewPanel'
import RemoveBgSidebar from './remove-bg/RemoveBgSidebar'

/**
 * Full-screen modal for removing the background from an image.
 * Two-panel layout: preview canvas (left) + controls sidebar (right).
 * Supports auto-remove (ML), manual brush erase/restore, zoom/pan, undo/redo.
 *
 * @param {{ isOpen: boolean, toggle: () => void, imageSrc: string|null, onApply: (blob: Blob) => void }} props
 */
function RemoveBgModal({isOpen, toggle, imageSrc, onApply}) {
    const {status, blob, error, retry} = useBackgroundRemoval(imageSrc)
    const editor = useRemoveBgEditor({imageSrc})

    // Reset editor on close, re-initialize on open
    useEffect(() => {
        if (!isOpen) {
            editor.resetEditor()
        } else if (imageSrc && !editor.ready) {
            editor.initWithOriginal(imageSrc)
        }
    }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

    // Apply ML result when ONNX completes
    useEffect(() => {
        if (blob && imageSrc && editor.ready) {
            editor.applyMlResult(imageSrc, blob)
        }
    }, [blob]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleApply = useCallback(async () => {
        try {
            const exportedBlob = await editor.exportBlob()
            if (!exportedBlob) return
            onApply(exportedBlob)
            toggle()
        } catch (err) {
            console.error('Failed to export canvas:', err)
        }
    }, [editor, onApply, toggle])

    const {handleKeyDown, handleKeyUp} = useRemoveBgShortcuts({
        editor,
        onApply: handleApply,
        onClose: toggle,
    })

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            className="CreateCustomShape__RemoveBgModal"
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            backdrop="static"
            size="lg"
        >
            <ModalBody>
                <RemoveBgEditorLayout>
                    <RemoveBgPreviewPanel editor={editor} status={status} error={error} retry={retry} />
                    <RemoveBgSidebar
                        onClose={toggle}
                        onAutoRemove={retry}
                        onApply={handleApply}
                        applyDisabled={!editor.ready}
                        loading={status === REMOVE_BG_STATUS.PROCESSING}
                        ready={editor.ready}
                        mode={editor.mode}
                        brushSize={editor.brushSize}
                        onSetMode={editor.setMode}
                        onSetBrushSize={editor.setBrushSize}
                        onRevertAll={editor.revertAll}
                        canUndo={editor.canUndo}
                    />
                </RemoveBgEditorLayout>
            </ModalBody>
        </Modal>
    )
}

export default RemoveBgModal

